/* eslint-disable @angular-eslint/directive-selector */
import {
  Component,
  Directive,
  Injector,
  Input,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { NodeRenderer } from 'ngx-contentful-rich-text';
import { ComponentTypes } from '../models/components.enum';
import { EmbeddedEntryService } from './embedded-entry.service';
import { setComponentInputs } from '../helper/component.helper';
import { CONTENTFUL_PROPS } from '../types';

/**
 * Simple component wrapper to allow the ngx-contentful-rich-text to render
 * nested rich text fields. It was added in order to provide a viewContainerRef
 * to the directive that maps the content type to a known angular component
 * within the white label ecosystem.
 */
@Component({
  selector: 'nibtravel-embedded-entry',
  template: `
    <ng-container
      [embeddedEntry]="fields"
      [type]="type"
      [props]="props"
    ></ng-container>
  `,
})
export class EmbeddedEntryComponent extends NodeRenderer implements OnInit {
  @Input() fields!: Record<string, any>;
  @Input() type!: string;
  @Input() props!: Record<string, any>;

  ngOnInit(): void {
    if (this.node?.data?.target) {
      const target = this.node.data.target;
      this.fields = target.fields;
      this.type = target.sys?.contentType?.sys.id;
    }
  }
}

/**
 * The directive responsible for resolving the component factory and
 * trigger the rendering in the correct location based on the viewContainerRef
 * which is a more elegant solution than tracking node position to render
 * the child component dynamically.
 *
 * The core logic resides into the EmbeddedEntryService.
 */
@Directive({ selector: '[embeddedEntry]' })
export class EmbeddedEntryDirective implements OnInit {
  @Input() embeddedEntry!: Record<string, any>;
  @Input() type!: ComponentTypes | string;
  @Input() props!: Record<string, any>;

  constructor(
    private host: ViewContainerRef,
    private entryService: EmbeddedEntryService,
    private parentInjector: Injector
  ) {}

  ngOnInit(): void {
    if (this.type) {
      const injector = this.props
        ? Injector.create({
            name: 'Embedded Entry',
            parent: this.parentInjector,
            providers: [
              {
                provide: CONTENTFUL_PROPS,
                useValue: this.props,
              },
            ],
          })
        : this.parentInjector;

      const component = this.entryService.createComponent(
        this.host,
        this.type,
        injector
      );
      if (component) {
        if (this.embeddedEntry) {
          setComponentInputs(component, this.embeddedEntry);
        }
      } else {
        console.warn(`Component type '${this.type}' not found`);
      }
    } else {
      console.warn('Not enough data to create dynamic component');
    }
  }
}
